import React from 'react';
import type { Plant } from '../../../../types/plant';
import { Sprout, ArrowRight, Ruler, Calendar } from 'lucide-react';
import { isFrostSensitive, isColdHardy } from '../../../../utils/dateUtils';

interface SowingPlantingProps {
  plant: Plant;
}

export default function SowingPlanting({ plant }: SowingPlantingProps) {
  // Determine if plant is frost sensitive or cold hardy
  const isPlantFrostSensitive = isFrostSensitive(plant.frost_tolerance);
  const isPlantColdHardy = isColdHardy(plant.frost_tolerance);

  // Get direct sow timing description
  const getDirectSowTiming = () => {
    if (isPlantFrostSensitive) {
      return "Start direct sowing exactly on your last frost date.";
    } else if (isPlantColdHardy) {
      const days = plant.direct_sow_cold_hardy_days_before_frost_max || 14;
      return `Can be direct sown ${days} days before your last frost date.`;
    } else {
      return "Start direct sowing on or after your last frost date.";
    }
  };

  return (
    <section className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-semibold mb-6">Sowing & Planting Guide</h2>
      <div className="space-y-6">
        <div className="border-b pb-6">
          <div className="flex items-center gap-2 mb-3">
            <Sprout className="h-5 w-5 text-primary-500" />
            <h3 className="text-lg font-medium">Starting from Seed</h3>
          </div>
          <p className="text-gray-600 mb-4">
            {plant.starting_from_seed_tips || 'No seed starting information available.'}
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-primary-500" />
              <h4 className="font-medium text-sm">Direct Sowing Timing</h4>
            </div>
            <p className="text-sm text-gray-600">{getDirectSowTiming()}</p>
            
            {isPlantFrostSensitive && (
              <div className="mt-2 text-sm text-orange-600 bg-orange-50 p-2 rounded">
                This plant is frost sensitive and should only be direct sown after all danger of frost has passed.
              </div>
            )}
            
            {isPlantColdHardy && (
              <div className="mt-2 text-sm text-blue-600 bg-blue-50 p-2 rounded">
                This plant is cold hardy and can be direct sown before the last frost date.
              </div>
            )}
          </div>
        </div>
        
        <div className="border-b pb-6">
          <div className="flex items-center gap-2 mb-3">
            <ArrowRight className="h-5 w-5 text-primary-500" />
            <h3 className="text-lg font-medium">Transplanting</h3>
          </div>
          <p className="text-gray-600">
            {plant.transplanting_tips || 'No transplanting information available.'}
          </p>
        </div>
        
        <div className="pb-6">
          <div className="flex items-center gap-2 mb-3">
            <Ruler className="h-5 w-5 text-primary-500" />
            <h3 className="text-lg font-medium">Spacing & Support</h3>
          </div>
          <div className="space-y-3">
            {plant.plant_spacing && (
              <p className="text-gray-600">
                <strong>Plant Spacing:</strong> {plant.plant_spacing}
              </p>
            )}
            {plant.support_requirements && (
              <p className="text-gray-600">
                <strong>Support Requirements:</strong> {plant.support_requirements}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}